/**
 * Pessoas, convites, perfis e permissoes por perfil.
 *
 * Reexportado por `../resources.ts`. Ver docs/FRONTEND.md, secao 3.
 */

import type { TenantSummary } from './auth';
import { query, type ListParams } from './comum';

import { api, requestBlob } from '../client';

export interface MemberRole {
  id: string;
  key: string;
  name: string;
  grants_all: boolean;
  /** O perfil restringe a pessoa a corretora ligada ao vinculo. */
  broker_scoped: boolean;
}

/**
 * Dados cadastrais do usuario, como o OnPoint os cadastra (P-USU-1). Ficam no
 * vinculo com a empresa. Texto vazio e "nao informado"; CPF, telefone e CEP chegam
 * so com os digitos.
 */
export interface UserPersonalData {
  cpf: string;
  phone: string;
  address_zip: string;
  address_street: string;
  address_number: string;
  address_complement: string;
  address_district: string;
  /** Sigla da UF. */
  address_state: string;
  /** Codigo IBGE do municipio. */
  address_city_code: string;
}

export interface Member extends UserPersonalData {
  id: string;
  user_id: string;
  name: string;
  email: string;
  status: string;
  roles: MemberRole[];
  created_at: string;
  last_login_at: string | null;
  suspended_at: string | null;
  suspend_reason: string;
  /** Corretora que a pessoa representa. So restringe quem tem perfil com escopo. */
  broker_id: string | null;
  /** Usuario principal do OnPoint (IsMain). So e lido. */
  is_primary: boolean;
  /** Chat de suporte desligado (OnPoint). So e lido. */
  support_chat_disabled: boolean;
}

/** Situacao do convite, deduzida no servidor a partir das datas. */
export type InvitationStatus = 'pendente' | 'aceito' | 'expirado' | 'revogado';

export interface Invitation extends Partial<UserPersonalData> {
  id: string;
  email: string;
  name: string;
  status: InvitationStatus;
  role_ids: string[];
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
  created_at: string;
  /** Corretora a que o aceite liga a pessoa. */
  broker_id?: string | null;
}

/**
 * Resultado de emitir ou reenviar um convite.
 *
 * O endereco de aceite chega uma vez so. Ele nao volta em nenhuma consulta: a
 * listagem de convites nao pode virar um caminho para recuperar o acesso de outra
 * pessoa.
 */
export interface InvitationIssued {
  invitation: Invitation;
  accept_url: string;
  delivered_by_email: boolean;
}

export interface InvitationPreview {
  email: string;
  name: string;
  known_identity: boolean;
  tenant: TenantSummary;
}

/** Convite com os dados do formulario Cadastrar Usuario (P-USU-1). */
export interface InvitationInput extends Partial<UserPersonalData> {
  email: string;
  name: string;
  role_ids: string[];
  /** Corretora a que o aceite liga a pessoa. A corretora nao escolhe: vale a dela. */
  broker_id?: string;
}

/** Publico do perfil: quem o recebe (userInfoTypeId no OnPoint). */
export type RoleAudience = 'seguradora' | 'corretora' | 'assessoria';

export interface Role {
  id: string;
  key: string;
  name: string;
  description: string;
  is_system: boolean;
  is_immutable: boolean;
  grants_all: boolean;
  broker_scoped: boolean;
  permissions: string[];
  /** Tela que abre no acesso. "/" e o Painel. */
  home_route: string;
  /** Perfil sugerido no cadastro de usuario, um por publico. */
  is_default: boolean;
  /** Perfil oferecido para ser concedido. */
  is_assignable: boolean;
  audience: RoleAudience;
}

/** Edicao parcial do perfil (Configurar perfil). */
export interface RoleUpdate {
  name?: string;
  description?: string;
  home_route?: string;
  is_default?: boolean;
  is_assignable?: boolean;
  /**
   * Troca a lista inteira de permissoes junto com o resto ("Editar perfil" do Perfil
   * da Corretora, P-PAR-4). Ausente nao mexe nas permissoes.
   */
  permissions?: string[];
}

/**
 * Cadastro do perfil. Sem os campos da corretora, o perfil nasce sem permissao; com
 * eles ("Cadastrar perfil" do Perfil da Corretora, P-PAR-4), nasce com o escopo, a
 * tela inicial e as permissoes de uma vez.
 */
export interface RoleCreate {
  name: string;
  description?: string;
  broker_scoped?: boolean;
  home_route?: string;
  permissions?: string[];
}

/** Edicao parcial do usuario. Campo ausente nao muda; texto vazio apaga. */
export type MemberUpdate = Partial<UserPersonalData> & { name?: string };

/** O envio do link de senha. O link nunca vem: so a pessoa o recebe. */
export interface PasswordLinkReceipt {
  delivered_by_email: boolean;
  expires_at: string;
}

/**
 * Perfil que quem chama pode conceder no convite e na troca de perfil. Para quem
 * administra a corretora, sao so os perfis da corretora.
 */
export type GrantableRole = Pick<
  Role,
  'id' | 'key' | 'name' | 'description' | 'grants_all' | 'broker_scoped'
> &
  Partial<Pick<Role, 'is_default' | 'is_assignable'>>;

export const members = {
  list: (params: ListParams, signal?: AbortSignal) =>
    api.get<Member[]>(`/api/v1/members${query(params)}`, signal),
  get: (memberId: string, signal?: AbortSignal) =>
    api.get<Member>(`/api/v1/members/${memberId}`, signal),
  update: (memberId: string, input: MemberUpdate) =>
    api.patch<Member>(`/api/v1/members/${memberId}`, input),
  /** O arquivo Exportar CSV, com os mesmos filtros da lista. */
  export: (params: ListParams) => requestBlob(`/api/v1/members/export${query(params)}`),
  grantableRoles: (signal?: AbortSignal) =>
    api.get<GrantableRole[]>('/api/v1/members/grantable-roles', signal),
  assignRole: (memberId: string, roleId: string) =>
    api.post<void>(`/api/v1/members/${memberId}/roles`, { role_id: roleId }),
  revokeRole: (memberId: string, roleId: string) =>
    api.delete<void>(`/api/v1/members/${memberId}/roles/${roleId}`),
  setRoles: (memberId: string, roleIds: string[]) =>
    api.put<void>(`/api/v1/members/${memberId}/roles`, { role_ids: roleIds }),
  setBroker: (memberId: string, brokerId: string | null) =>
    api.put<void>(`/api/v1/members/${memberId}/broker`, { broker_id: brokerId }),
  suspend: (memberId: string, reason: string) =>
    api.post<void>(`/api/v1/members/${memberId}/suspend`, { reason }),
  deactivate: (memberId: string, reason: string) =>
    api.post<void>(`/api/v1/members/${memberId}/deactivate`, { reason }),
  reactivate: (memberId: string) =>
    api.post<void>(`/api/v1/members/${memberId}/reactivate`, {}),
  /** Manda a pessoa o link de redefinicao de senha. Ninguem ve a senha dela. */
  sendPasswordLink: (memberId: string) =>
    api.post<PasswordLinkReceipt>(`/api/v1/members/${memberId}/password-reset`),
  remove: (memberId: string) => api.delete<void>(`/api/v1/members/${memberId}`),
};

export const invitations = {
  list: (signal?: AbortSignal) => api.get<Invitation[]>('/api/v1/invitations', signal),
  create: (input: InvitationInput) =>
    api.post<InvitationIssued>('/api/v1/invitations', input),
  resend: (invitationId: string) =>
    api.post<InvitationIssued>(`/api/v1/invitations/${invitationId}/resend`, {}),
  revoke: (invitationId: string) =>
    api.delete<void>(`/api/v1/invitations/${invitationId}`),

  // O aceite acontece antes de existir sessao: o segredo do convite e a credencial
  // da chamada, e por isso estas duas nao mandam token nenhum.
  preview: (token: string, signal?: AbortSignal) =>
    api.get<InvitationPreview>(
      `/api/v1/auth/invitations/${encodeURIComponent(token)}`,
      signal,
    ),
  accept: (token: string, input: { name: string; password: string }) =>
    api.post<void>(
      `/api/v1/auth/invitations/${encodeURIComponent(token)}/accept`,
      input,
    ),
};

export const roles = {
  list: (signal?: AbortSignal, audience?: RoleAudience) =>
    api.get<Role[]>(`/api/v1/roles${query({ audience })}`, signal),
  get: (roleId: string, signal?: AbortSignal) =>
    api.get<Role>(`/api/v1/roles/${roleId}`, signal),
  create: (input: RoleCreate) => api.post<Role>('/api/v1/roles', input),
  update: (roleId: string, input: RoleUpdate) =>
    api.patch<Role>(`/api/v1/roles/${roleId}`, input),
  /** Quem tem o perfil (secao Usuarios do quadro). */
  members: (roleId: string, signal?: AbortSignal) =>
    api.get<Member[]>(`/api/v1/roles/${roleId}/members`, signal),
  remove: (roleId: string) => api.delete<void>(`/api/v1/roles/${roleId}`),
};

// --- Permissoes por papel ---------------------------------------------------

/** Uma permissao do catalogo global. */
export interface Permission {
  key: string;
  module: string;
  resource: string;
  action: string;
  description: string;
}

export const permissions = {
  /**
   * Com o publico da corretora, so as permissoes que valem para quem tem escopo de
   * corretora: a matriz do perfil da corretora nao oferece o que nao teria efeito.
   */
  list: (signal?: AbortSignal, audience?: RoleAudience) =>
    api.get<Permission[]>(`/api/v1/permissions${query({ audience })}`, signal),
};

export const rolePermissions = {
  set: (roleId: string, permissions: string[]) =>
    api.put<Role>(`/api/v1/roles/${roleId}/permissions`, { permissions }),
};

// --- Endereco do usuario (P-USU-1) ------------------------------------------

/** UF do catalogo da plataforma (P-FUN-1). */
export interface UserAddressState {
  code: string;
  ibge_code: string;
  name: string;
}

/** Municipio do catalogo da plataforma (P-FUN-1). */
export interface UserAddressCity {
  ibge_code: string;
  state_code: string;
  name: string;
}

/**
 * UF e cidade do endereco do usuario, dos catalogos da plataforma. A cidade so
 * existe dentro da UF, como no OnPoint, que desabilita a Cidade ate escolher a UF.
 */
export const userAddress = {
  states: (signal?: AbortSignal) =>
    api.get<UserAddressState[]>('/api/v1/catalogs/states', signal),
  cities: (stateCode: string, signal?: AbortSignal) =>
    api.get<UserAddressCity[]>(
      `/api/v1/catalogs/states/${encodeURIComponent(stateCode)}/cities`,
      signal,
    ),
};
